import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Preloader from 'components/Preloader';
import EmptyContent from 'components/EmptyContent';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import Card from 'components/Card/Card';

import { getClientId } from 'utils';
import { Button, notification } from 'ui-library';
import { downloadDmsDocument } from 'utils/downloadDmsDocument';
import { EXTERNAL_PRODUCTS } from 'constants/constants';
import arrayContains from 'utils/arrayUtils';
import { Details } from './components/Details';
import { useAllPortfolios } from './useAllPortfolios';
import { useSinglePortfolio } from '../../context';

import './Deposit.css';

const Deposit = () => {
    const { t, i18n: { language } } = useTranslation();
    const { dfsClientId, portfolioId } = useParams();
    const clientId = useMemo(() => (getClientId(dfsClientId)), [dfsClientId]);

    const {
        isLoading,
        error: portfoliosError,
        data,
    } = useAllPortfolios(clientId, language);

    const { portfolioDetail, productType } = useSinglePortfolio() as any;
    const { mutateAsync: fetchQrPaymentSlip, isPending: inProgress } = useGenericAPIMutation();

    const onDownloadButtonClick = useCallback(async () => {
        try {
            const { data: dta } = await fetchQrPaymentSlip({
                service: 'customDocumentsService',
                API: 'getQrPaymentSlip',
                payload: { portfolioId },
            });

            if (dta?.pdf) {
                downloadDmsDocument({ file: dta?.pdf, mimeType: 'application/pdf', originalFilename: `${t('client.portfolios.deposit.documentName')}_${portfolioId}.pdf` }, { saveFile: true });
            } else {
                notification.open({ content: t('general.somethingWentWrong'), type: 'error' });
            }
        } catch {
            // error notification handled by useGenericAPIMutation onError
        }
    }, [portfolioId, t, fetchQrPaymentSlip]);

    return (
        <Preloader isLoading={isLoading} error={portfoliosError}>
            <EmptyContent data={data} text="Not Found">
                {!!data.length && (
                    <div className="container">
                        <div className="deposit-container">
                            <div className="deposit-column">
                                <Details clientId={clientId} portfolioId={portfolioId} currency={portfolioDetail?.currency ?? 'CHF'} />
                            </div>
                            {arrayContains([EXTERNAL_PRODUCTS.aaa, EXTERNAL_PRODUCTS.aaap], productType) && (
                                <div className="deposit-column">
                                    <Card>
                                        <Button loading={inProgress} disabled={inProgress} onClick={onDownloadButtonClick} type="primary">{t('client.portfolios.deposit.qrButton.download')}</Button>
                                    </Card>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </EmptyContent>
        </Preloader>
    );
};

export default Deposit;
